import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../table/Table";
import VoiceIcon from "../../img/voice-icon.svg";
import { useSpeechSynthesis } from "react-speech-kit";
import "./SorteoView.css";

const getChar = (number) => {
  if (number < 15) return "B";
  if (number < 30) return "I";
  if (number < 45) return "N";
  if (number < 60) return "G";
  return "O";
};

const sample = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Helpers para normalizar y deduplicar winners
const normalizeGanador = (g) => ({
  serie: g?.serie ?? null,
  carton: g?.carton ?? null,
  ticketId: Number.isFinite(Number(g?.ticketId)) ? Number(g.ticketId) : null,
  posicion: Number.isFinite(Number(g?.posicion)) ? Number(g.posicion) : null,
  qr_code:
    typeof g?.qr_code === "string"
      ? g.qr_code.trim()
      : typeof g?.qr === "string"
        ? g.qr.trim()
        : "",
});

const winnerKey = (g) => {
  return g.qr_code && g.qr_code.length
    ? `qr:${g.qr_code}`
    : `serie:${g.serie}|carton:${g.carton}|id:${g.ticketId ?? g.posicion ?? ""}`;
};

const uniqueWinners = (arr) => {
  const seen = new Set();
  const out = [];
  for (const raw of arr || []) {
    const g = normalizeGanador(raw);
    const key = winnerKey(g);
    if (!seen.has(key)) {
      seen.add(key);
      out.push(g);
    }
  }
  return out;
};

const sameWinnerSet = (a, b) => {
  const keysA = (a || []).map(winnerKey).sort();
  const keysB = (b || []).map(winnerKey).sort();
  return JSON.stringify(keysA) === JSON.stringify(keysB);
};

const safePosition = (g) => {
  const t = Number(g?.ticketId);
  if (Number.isFinite(t)) return t + 1;
  const p = Number(g?.posicion);
  if (Number.isFinite(p)) return p;
  return null;
};

const displayCarton = (g) => {
  const pos = safePosition(g);
  if (g?.carton) return g.carton;
  return pos ? `posición nº ${pos}` : "desconocido";
};

const displayQR = (g, truncarQR) => {
  const qr = g?.qr_code ?? "";
  return typeof qr === "string" && qr.trim() ? truncarQR(qr.trim()) : "sin QR";
};

const iniciarCronometro = (setBloquearSortear, setContador) => {
  setBloquearSortear(true);
  setContador(10);

  const interval = setInterval(() => {
    setContador((prev) => {
      if (prev <= 1) {
        clearInterval(interval);
        setBloquearSortear(false);
        return 0;
      }
      return prev - 1;
    });
  }, 1000);
};

const SorteoView = () => {
  const [contador, setContador] = useState(0);
  const [bloquearSortear, setBloquearSortear] = useState(false);

  const navigate = useNavigate();

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem("accessToken");
    navigate("/login");
  }, [navigate]);

  const [squares, setSquares] = useState(
    Array.from(Array(75)).map((_, idx) => ({
      id: idx + 1,
      char: getChar(idx),
      state: false,
      orden: null,
    })),
  );

  // ✅ CAMBIO: Lógica de botones basada en el estado del backend
  const [isButtonDisable, setIsButtonDisable] = useState(false);
  const [isButtonCloseDisable, setIsButtonCloseDisable] = useState(true);

  const [nombreSorteo, setNombreSorteo] = useState("");
  const [fechaSorteo, setFechaSorteo] = useState("");
  const [sorteoIdMongo, setSorteoIdMongo] = useState("");
  const [patronActual, setPatronActual] = useState(null);

  const [currentSorted, setCurrentSorted] = useState(null);
  const [ganadores, setGanadores] = useState([]);
  const [showAlerta, setShowAlerta] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [ganadoresConfirmados, setGanadoresConfirmados] = useState(new Set());

  // ✅ NUEVO: Estado del backend (fuente de verdad)
  const [estadoBackend, setEstadoBackend] = useState(null);

  const [tiradasList, setTiradasList] = useState([]);

  const { speak, voices } = useSpeechSynthesis();
  const API_URL = process.env.REACT_APP_API_URL;

  const winnersPrevios = useRef([]);
  const alertaMostrada = useRef(false);

  // ==========================================
  // ✅ NUEVO: Consultar estado REAL del backend
  // ==========================================
  const consultarEstadoBackend = useCallback(async () => {
    if (!nombreSorteo || !fechaSorteo) return null;

    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(
        `${API_URL}/sorteo/estado_ganadores?nombreSorteo=${encodeURIComponent(
          nombreSorteo,
        )}&fechaSorteo=${encodeURIComponent(fechaSorteo)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return null;
      }

      if (!res.ok) return null;

      const data = await res.json();
      setEstadoBackend(data);
      return data;
    } catch (error) {
      console.error("Error consultando estado backend:", error);
      return null;
    }
  }, [API_URL, nombreSorteo, fechaSorteo, handleUnauthorized]);

  // ==========================================
  // ✅ NUEVO: Polling al backend cada 2 segundos
  //     (SOLO cuando hay ganador detectado)
  // ==========================================
  useEffect(() => {
    if (!showAlerta) return;

    const interval = setInterval(async () => {
      const data = await consultarEstadoBackend();
      if (data) {
        // Actualizar tiempo restante desde el backend
        if (data.hay_ganador) {
          setCountdown(data.tiempo_restante_seg);
        }
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [showAlerta, consultarEstadoBackend]);

  // ==========================================
  // ✅ NUEVO: Lógica del botón basada en estadoBackend
  // ==========================================
  useEffect(() => {
    if (!estadoBackend) return;

    const { hay_ganador, hay_ganador_sin_confirmar, tiempo_restante_seg } =
      estadoBackend;

    if (!hay_ganador) {
      // No hay ganador: permitir sortear
      setIsButtonDisable(false);
      setIsButtonCloseDisable(true);
      return;
    }

    if (hay_ganador && !hay_ganador_sin_confirmar) {
      // ✅ Todos los ganadores confirmaron
      // Botón "Sortear" bloqueado, "Cerrar tiradas" habilitado
      setIsButtonDisable(true);
      setIsButtonCloseDisable(false);
      return;
    }

    if (hay_ganador && hay_ganador_sin_confirmar) {
      // ⚠️ Hay ganador(es) sin confirmar
      if (tiempo_restante_seg > 0) {
        // Aún hay tiempo: bloqueado esperando
        setIsButtonDisable(true);
        setIsButtonCloseDisable(true); // ❌ Bloqueado hasta que confirmen o expire
      } else {
        // ⏰ Tiempo agotado: admin puede forzar cierre
        setIsButtonDisable(true);
        setIsButtonCloseDisable(false); // ✅ Puede cerrar
      }
    }
  }, [estadoBackend]);

  // Resetear estado cuando cambia el patrón
  useEffect(() => {
    if (patronActual?._id && patronActual._id !== "sin_patron") {
      setGanadores([]);
      setGanadoresConfirmados(new Set());
      setShowAlerta(false);
      setCountdown(0);
      setEstadoBackend(null);
      if (!ganadoresConfirmados.size) {
        setIsButtonDisable(false);
      }
      alertaMostrada.current = false;
      winnersPrevios.current = [];
    }
  }, [patronActual?._id]);

  // ==========================================
  // ✅ MODIFICADO: Verificar ganadores al montar
  //     Ahora usa el endpoint unificado
  // ==========================================
  useEffect(() => {
    if (
      !patronActual ||
      !patronActual._id ||
      patronActual._id === "sin_patron"
    ) {
      setGanadores([]);
      setGanadoresConfirmados(new Set());
      setShowAlerta(false);
      setCountdown(0);
      setIsButtonDisable(false);
      alertaMostrada.current = false;
      return;
    }

    // ✅ Usar el nuevo endpoint que ya tiene todo integrado
    const verificarEstado = async () => {
      const data = await consultarEstadoBackend();
      if (!data) return;

      if (data.hay_ganador && Array.isArray(data.ganadores)) {
        // Filtrar solo winners con QR válido
        const winnersConQR = data.ganadores.filter(
          (w) => w?.qr_code && w.qr_code.trim().length > 0,
        );

        if (winnersConQR.length === 0) {
          setGanadores([]);
          setGanadoresConfirmados(new Set());
          setShowAlerta(false);
          setCountdown(0);
          setIsButtonDisable(false);
          setIsButtonCloseDisable(true);
          alertaMostrada.current = false;
          return;
        }

        // Actualizar confirmados
        const qrConfirmados = new Set(
          winnersConQR.filter((w) => w.confirmado).map((w) => w.qr_code),
        );

        if (qrConfirmados.size > 0) {
          setGanadoresConfirmados(qrConfirmados);
          setShowAlerta(false);
          setCountdown(0);
          setIsButtonDisable(true);
          setIsButtonCloseDisable(false);
          alertaMostrada.current = false;
        }
      } else {
        setGanadores([]);
        setGanadoresConfirmados(new Set());
        setShowAlerta(false);
        setCountdown(0);
        setIsButtonDisable(false);
        setIsButtonCloseDisable(true);
        alertaMostrada.current = false;
      }
    };

    verificarEstado();
  }, [patronActual?._id, nombreSorteo, fechaSorteo, sorteoIdMongo, consultarEstadoBackend]);

  // 🔄 Resetear cuando no hay patrón activo
  useEffect(() => {
    if (!patronActual || patronActual._id === "sin_patron") {
      setGanadores([]);
      setGanadoresConfirmados(new Set());
      setShowAlerta(false);
      setCountdown(0);
      setIsButtonDisable(false);
      alertaMostrada.current = false;
      setEstadoBackend(null);
    }
  }, [patronActual]);

  // Verificar ganador (mantener tu función original)
  const verificarGanador = useCallback(async () => {
    if (!patronActual || !patronActual._id) return;
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      const res = await fetch(`${API_URL}/verificar_ganador`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          nombreSorteo,
          fechaSorteo,
          patronId: patronActual._id,
        }),
      });

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      const data = await res.json();

      if (data.hayGanador && Array.isArray(data.ganadores)) {
        const dedup = uniqueWinners(data.ganadores);

        if (!sameWinnerSet(dedup, winnersPrevios.current)) {
          setGanadores(dedup);
          winnersPrevios.current = dedup;

          // ✅ Activar alerta con datos frescos del backend
          if (!alertaMostrada.current) {
            const fanfare = new Audio("/sounds/ganador.mp3");
            fanfare.play();

            setTimeout(() => {
              setShowAlerta(true);
              setIsButtonDisable(true);
              setIsButtonCloseDisable(true); // Inicia bloqueado
              alertaMostrada.current = true;
              // ✅ Consultar estado del backend inmediatamente
              consultarEstadoBackend();
            }, 1500);
          }
        }
      } else if (!data.hayGanador) {
        if (winnersPrevios.current.length > 0) {
          setGanadores([]);
          winnersPrevios.current = [];
          setShowAlerta(false);
          setCountdown(0);
          alertaMostrada.current = false;
        }
      }
    } catch (error) {
      console.error("Error al verificar winner:", error);
    }
  }, [
    API_URL,
    nombreSorteo,
    fechaSorteo,
    patronActual,
    handleUnauthorized,
    consultarEstadoBackend,
  ]);

  const verificarGanadorRef = useRef();
  verificarGanadorRef.current = verificarGanador;

  // Cargar info del sorteo y tiradas previas
  useEffect(() => {
    const fetchData = async () => {
      try {
        const resInfo = await fetch(`${API_URL}/info_sorteo_actual`);
        const info = await resInfo.json();

        setNombreSorteo(info.nombreSorteo);
        setFechaSorteo(info.fechaSorteo);
        const rawId = info.idSorteo;
        const sid =
          typeof rawId === "string" &&
          rawId.trim().length === 24 &&
          /^[a-fA-F0-9]+$/i.test(rawId.trim())
            ? rawId.trim()
            : "";
        setSorteoIdMongo(sid);

        const patronObj = info.patronActivo
          ? {
              _id: info.patronActivo,
              nombre: info.patronActivo,
              descripcion: info.descriPatron || "",
            }
          : null;

        setPatronActual(patronObj);

        if (patronObj && patronObj._id) {
          const resTiradas = await fetch(
            `${API_URL}/tiradas_sorteo/${info.nombreSorteo}/${info.fechaSorteo}/${patronObj._id}`,
          );
          const tiradasData = await resTiradas.json();

          const dataOrdenada = [...tiradasData].sort(
            (b, a) => b.orden - a.orden,
          );

          setTiradasList(dataOrdenada);

          if (Array.isArray(tiradasData) && tiradasData.length > 0) {
            setSquares((prev) =>
              prev.map((square) => {
                const tirada = tiradasData.find(
                  (t) => t.ficha === `${square.char}${square.id}`,
                );
                return tirada
                  ? { ...square, state: true, orden: tirada.orden }
                  : square;
              }),
            );

            const ultimaPorOrden = tiradasData.reduce(
              (max, t) =>
                max === null || (t?.orden ?? 0) > (max?.orden ?? 0) ? t : max,
              null,
            );

            if (ultimaPorOrden) {
              setCurrentSorted({
                ficha: ultimaPorOrden.ficha,
                char: ultimaPorOrden.ficha[0],
                id: parseInt(ultimaPorOrden.ficha.slice(1), 10),
                orden: ultimaPorOrden.orden,
              });
            }
          }

          verificarGanadorRef.current();
        }
      } catch (error) {
        console.error("Error al cargar sorteo:", error);
      }
    };

    fetchData();
  }, [API_URL]);

  // Función para sortear nueva ficha
  const sortNumber = () => {
    if (
      patronActual?._id === "sin_patron" ||
      patronActual?._id === "Sin patrón activo"
    ) {
      alert("⚠️ No se puede sortear mientras este sin patrón activo.");
      return;
    }

    if (ganadoresConfirmados.size > 0) {
      alert("⚠️ Ya hay un winner confirmado. Cierra las tiradas para continuar.");
      return;
    }

    if (!patronActual?._id) {
      console.error("No se puede guardar tirada: patronActual._id está vacío");
      return;
    }

    const unsorted = squares.filter((square) => !square.state);
    if (unsorted.length === 0) {
      alert("Todos los números han sido sorteados.");
      setIsButtonCloseDisable(false);
      return;
    }

    let newSorted;
    do {
      newSorted = sample(unsorted);
    } while (newSorted && newSorted.char === currentSorted?.char);

    if (newSorted) {
      const ordenActual = Math.max(...squares.map((s) => s.orden || 0));
      const nuevaFichaObj = {
        ficha: `${newSorted.char}${newSorted.id}`,
        orden: ordenActual + 1,
      };

      fetch(`${API_URL}/last_draw`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ficha: `${newSorted.char}${newSorted.id}`,
          nombreSorteo,
          fechaSorteo,
          orden: ordenActual + 1,
          idPatron: patronActual._id,
        }),
      }).catch((err) => console.error("Error al guardar tirada:", err));

      setTiradasList((prev) => [nuevaFichaObj, ...prev]);

      setSquares((prev) =>
        prev.map((square) =>
          square.id === newSorted.id
            ? { ...square, state: true, orden: ordenActual + 1 }
            : square,
        ),
      );

      setCurrentSorted({
        ...newSorted,
        ficha: `${newSorted.char}${newSorted.id}`,
        orden: ordenActual + 1,
      });

      if (speak) {
        speak({
          text: `${newSorted.char} ${newSorted.id}`,
          voice: voices.find((v) => v.lang === "es-ES"),
          rate: 0.9,
          pitch: 1,
          volume: 1,
        });
      }

      verificarGanador();
    }
  };

  // ==========================================
  // ✅ NUEVO: Cuando expira el tiempo, consultar backend una vez
  // ==========================================
  useEffect(() => {
    if (countdown > 0) return;
    if (!showAlerta) return;
    if (!alertaMostrada.current) return;

    // Cuando el countdown llega a 0, forzar consulta al backend
    const verificarExpiracion = async () => {
      const data = await consultarEstadoBackend();
      if (!data) return;

      // Si el backend dice que aún hay alguien sin confirmar y tiempo=0,
      // significa que expiró sin confirmación
      if (
        data.hay_ganador &&
        data.hay_ganador_sin_confirmar &&
        data.tiempo_restante_seg === 0
      ) {
        console.log("⏰ Tiempo agotado - Permitiendo cerrar");
        // El useEffect de estadoBackend se encargará de habilitar el botón
      }
    };

    verificarExpiracion();
  }, [countdown, showAlerta, consultarEstadoBackend]);

  // ✅ MANTENEMOS: Countdown local (visual solamente)
  useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  // Función para desactivar ganadores que no confirmaron
  const desactivarGanadoresNoConfirmados = async () => {
    if (!sorteoIdMongo || !patronActual?._id) return;
    if (!ganadores.length) return;

    try {
      const token = localStorage.getItem("access_token");
      const winnersNoConfirmados = (ganadores || []).filter(
        (w) => w?.qr_code && !ganadoresConfirmados.has(w.qr_code),
      );

      for (const w of winnersNoConfirmados) {
        try {
          await fetch(
            `${API_URL}/participaciones/desactivar?sorteo_id=${encodeURIComponent(
              sorteoIdMongo,
            )}&patron_id=${encodeURIComponent(
              patronActual._id,
            )}&carton_id=${encodeURIComponent(w.qr_code)}`,
            {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
            },
          );
        } catch (e) {
          console.error("Error al desactivar:", e);
        }
      }

      setGanadores([]);
      setShowAlerta(false);
      setCountdown(0);
      alertaMostrada.current = false;
      winnersPrevios.current = [];
      setGanadoresConfirmados(new Set());
      setIsButtonDisable(false);
      setEstadoBackend(null);
    } catch (error) {
      console.error("Error al desactivar ganadores:", error);
    }
  };

  const cerrarTiradas = async () => {
    console.log("⚠️ Iniciando cierre de tiradas...");
    alert("⚠️ Cerrando tiradas... Por favor espere.");

    try {
      const token = localStorage.getItem("access_token");

      const url = `${API_URL}/mover_tiradas_a_historial`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Error ${res.status}: ${errorText}`);
      }

      await fetch(`${API_URL}/patron_activate/${patronActual?._id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ activo: false }),
      });

      const sinPatronId = "sin_patron";
      await fetch(`${API_URL}/patron_activate/${sinPatronId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ activo: true }),
      });

      alert("🎉 Tiradas cerradas y movidas al historial.");
      setIsButtonCloseDisable(true);
      setShowAlerta(false);
      setGanadores([]);
      setGanadoresConfirmados(new Set());
      setCurrentSorted(null);
      setSquares((prev) =>
        prev.map((s) => ({ ...s, state: false, orden: null })),
      );
      alertaMostrada.current = false;
      winnersPrevios.current = [];
      setPatronActual(null);
      setTiradasList([]);
      setEstadoBackend(null);
    } catch (error) {
      console.error("⚠️ Error al cerrar tiradas:", error);
      alert("⚠️ Error al cerrar tiradas: " + error.message);
    }
  };

  const truncarQR = (qr) =>
    typeof qr === "string" && qr.length > 20
      ? `${qr.slice(0, 10)}...${qr.slice(-10)}`
      : qr;

  return (
    <div className="Sorteo">
      <div className="container">
        <div className="sorter">
          <div className="sorteo-info">
            <div className="sorteo-nombre">{nombreSorteo}</div>
            <div className="sorteo-fecha">Fecha del sorteo: {fechaSorteo}</div>
          </div>

          <div className="patron-info">
            <strong>Patrón actual:</strong>{" "}
            {typeof patronActual === "string"
              ? patronActual
              : patronActual?.nombre || "Sin patrón activo"}
          </div>

          <div className="ficha-actual">
            {currentSorted
              ? `${currentSorted.ficha} (orden ${currentSorted.orden})`
              : "Aún no se ha sorteado ninguna ficha"}
          </div>

          <div className="historial-sidebar-compacto">
            <div className="historial-header-mini">Panel de Secuencia</div>
            <div className="historial-scroll-horizontal">
              <div className="flex-columns-wrapper">
                {Array.from({
                  length: Math.ceil(tiradasList.length / 15),
                }).map((_, colIndex) => (
                  <div key={colIndex} className="col-mini-bingo">
                    <ul className="list-fichas-compact">
                      {tiradasList
                        .slice(colIndex * 15, colIndex * 15 + 15)
                        .map((t) => (
                          <li key={t.orden} className="item-ficha-mini">
                            <span
                              className={`circle-mini ${
                                t.ficha.startsWith("B") ||
                                t.ficha.startsWith("G")
                                  ? "bg-v"
                                  : "bg-r"
                              }`}
                            >
                              {t.ficha[0]}
                            </span>
                            <span className="text-ficha-mini">{t.ficha}</span>
                            <span className="text-orden-mini">#{t.orden}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <button
            className={`btn ${
              bloquearSortear || isButtonDisable ? "disabled-button" : ""
            }`}
            onClick={() => {
              if (!bloquearSortear && !isButtonDisable) {
                sortNumber();
                iniciarCronometro(setBloquearSortear, setContador);
              }
            }}
            disabled={bloquearSortear || isButtonDisable}
          >
            {bloquearSortear ? `Espera ${contador}s` : "Sortear"}
          </button>

          {bloquearSortear && (
            <div style={{ marginTop: "8px", color: "red" }}>
              Espera {contador} segundos para volver a sortear
            </div>
          )}

          {isButtonDisable && !bloquearSortear && (
            <div
              style={{ marginTop: "8px", color: "purple", fontWeight: "bold" }}
            >
              ⚠️ {estadoBackend?.hay_ganador_sin_confirmar
                ? `Esperando confirmación de ganador(es)... (${countdown}s)`
                : "Hay al menos un ganador detectado"}
            </div>
          )}

          <br />

          <button
            className={`btn ${isButtonDisable ? "disabled-button" : ""}`}
            onClick={() =>
              speak({
                text: `${currentSorted?.char} ${currentSorted?.id}`,
                voice: voices.find((v) => v.lang === "es-ES"),
                rate: 0.9,
                pitch: 1,
                volume: 1,
              })
            }
            disabled={isButtonDisable || !currentSorted}
          >
            <img src={VoiceIcon} alt="Leer número" />
          </button>

          {showAlerta &&
            (() => {
              const winnersParaMostrar = (ganadores || []).filter(
                (w) => w?.qr_code && w.qr_code.trim().length > 0,
              );
              if (winnersParaMostrar.length === 0) return null;

              return (
                <div className="alerta-ganador">
                  🎉 ¡
                  {winnersParaMostrar.length === 1 ? "Ganador" : "Ganadores"}{" "}
                  detectado
                  {winnersParaMostrar.length === 1 ? "" : "s"}!
                  <div className="countdown-timer">
                    ⏳ {estadoBackend?.hay_ganador_sin_confirmar
                      ? `Espera: ${countdown}s`
                      : "✓ Ganador confirmó"}
                  </div>
                  <ul>
                    {winnersParaMostrar.map((g, idx) => (
                      <li key={idx}>
                        Serie: {g?.serie ?? "—"} | Cartón: {displayCarton(g)} |{" "}
                        QR: {displayQR(g, truncarQR)}
                        {ganadoresConfirmados.has(g.qr_code) ? (
                          <span
                            style={{
                              marginLeft: "10px",
                              color: "green",
                              fontWeight: "bold",
                            }}
                          >
                            ✓ Confirmado
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

          <button
            type="button"
            className={`cerrar-tiradas ${
              isButtonCloseDisable ? "disabled" : ""
            }`}
            onClick={() => {
              if (isButtonCloseDisable) {
                return;
              }

              // ✅ Si hay ganadores sin confirmar y el tiempo expiró, desactivar primero
              if (
                estadoBackend?.hay_ganador_sin_confirmar &&
                estadoBackend?.tiempo_restante_seg === 0
              ) {
                if (
                  window.confirm(
                    "⏰ El tiempo expiró sin confirmación. ¿Cerrar y desactivar a los ganadores que no cantaron?",
                  )
                ) {
                  desactivarGanadoresNoConfirmados().then(() => cerrarTiradas());
                }
              } else {
                cerrarTiradas();
              }
            }}
            disabled={isButtonCloseDisable}
          >
            Cerrar tiradas {isButtonCloseDisable ? "🔒" : "🔓"}
          </button>
        </div>

        <Table squares={squares} />
      </div>
    </div>
  );
};

export default SorteoView;